/**
 * 作り手の「どの広告を出すか」（バナー広告のカードの中・2026-10-22 money/self-serve-ads-dash）。設計 §3「作り手が選べること」・決めること ③。
 *
 *   (●) KUMO の作り手の広告 ＋ Adsterra（おすすめ・既定）
 *   ( ) KUMO の作り手の広告だけ（無い時は何も出さない）
 *   ( ) Adsterra だけ（ほかの作り手の広告を出さない）
 *
 * 口: 約束 §2 GET / PUT /v1/money/self-ads/hosts/:projectId（{ source?, notified? }）。フラグ off（404）なら何も出さない = 今までのカードのまま。
 * お知らせ: notifiedAt が null（まだ見ていない）なら、選択の上に「ほかの作り手の広告も出るようになりました」を出し、「わかりました」で notified: true。
 *   WHY: すでにバナーを ON にしている作り手は、自分で選ばないうちに既定（両方）で KUMO の作り手の広告が出始める = 先に知らせる（設計 §3・決めること ③）。
 * 選ぶとすぐ保存（確認のダイアログは出さない）: お金が動かない・いつでも戻せる・本番の設定（config）でもない（DisplayAdsCard の ON / OFF とは別）。
 * 置く所: DisplayAdsCard の「通常バナー」が ON で、本番の画面のときだけ（この選択はバナーの帯にだけ効く。Social Bar には効かない）。
 */
import { useEffect, useState } from 'react';
import type { ApiError } from '../api';
import { errorText, useT } from '../i18n';
import { getHostPref, isOff, putHostPref } from '../selfAdsApi';
import { HOST_SOURCES, type HostPref, type HostSource } from '../selfAds';

export function SelfAdHostChoice({ projectId }: { projectId: string }) {
  const { t } = useT();
  const [pref, setPref] = useState<HostPref | null>(null);
  const [off, setOff] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setPref(null);
    getHostPref(projectId)
      .then((p) => live && setPref(p))
      .catch((e) => {
        if (!live) return;
        if (isOff(e)) setOff(true);
        else setErr(errorText(e as ApiError));
      });
    return () => {
      live = false;
    };
  }, [projectId]);

  if (off) return null;
  if (!pref) return err ? <p className="alert alert-error small">{err}</p> : null;

  const save = async (body: { source?: HostSource; notified?: boolean }) => {
    setBusy(true);
    setErr(null);
    setSaved(false);
    try {
      setPref(await putHostPref(projectId, body));
      if (body.source) setSaved(true);
    } catch (e) {
      setErr(errorText(e as ApiError));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ssa-host-choice">
      {pref.notifiedAt === null && (
        <div className="alert alert-info" role="status">
          <strong>{t('ssa.host.newTitle')}</strong>
          <span>{t('ssa.host.newBody')}</span>
          <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={() => void save({ notified: true })}>
            {t('ssa.host.gotIt')}
          </button>
        </div>
      )}
      <fieldset className="choice-group ssa-host-options" disabled={busy}>
        <legend>{t('ssa.host.legend')}</legend>
        {/* 前提（自分の広告・子ども向けは出ない・100%）は選ぶ前に読めるよう見出しの直下（見た目の確認 2026-10-22） */}
        <p className="small muted ssa-host-note">{t('ssa.host.note')}</p>
        {HOST_SOURCES.map((s) => (
          <label key={s} className={`choice ${pref.source === s ? 'is-active' : ''}`}>
            <input
              type="radio"
              name={`ssa-host-${projectId}`}
              checked={pref.source === s}
              // 選んだらお知らせも見たことにする（選んだ = 知っている）
              onChange={() => void save(pref.notifiedAt === null ? { source: s, notified: true } : { source: s })}
            />
            <span>
              <strong>
                {t(`ssa.host.${s}`)}
                {s === 'kumo_and_adsterra' && <span className="pill pill-ok ssa-host-rec">{t('ssa.host.recommended')}</span>}
              </strong>
              <span className="small muted">{t(`ssa.host.${s}.help`)}</span>
            </span>
          </label>
        ))}
      </fieldset>
      {saved && (
        <p className="small muted" aria-live="polite">
          {t('ssa.saved')}
        </p>
      )}
      {err && <p className="alert alert-error small">{err}</p>}
    </div>
  );
}
